import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  ButtonMode,
  DocumentIcon,
  DownloadIcon,
  Popover,
  PagePanel,
  ButtonSize,
  NotificationType,
  Notification,
  LoadingSpinner
} from 'edenred-ui';
import { useTheme } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { DropdownOptionAPI } from '@ingestro/importer-react';

import { routerPaths } from '@epi-constants/routerPaths';
import { getTemplateUrl } from '@epi-pages/Beneficiaries/helpers/getTemplateUrl';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { FileFormatEnum } from '@epi-models/pages/Beneficiaries';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import { useCancelImportMutation } from '@epi-api/massUpload/massUpload';
import { selectImportStatus } from '@epi-store/beneficiariesImport/beneficiariesImport.selectors';
import { useGetDetailsQuery } from '@epi-api/user';

import { IngestroImporter, IngestroError } from './components';
import {
  FileFormatButton,
  IngestroWrapper,
  SimpleLink
} from './ImportEmployees.styles';
import { useImportEmployees } from './useImportEmployees';
import { AnotherUploadInProgress } from './components/AnotherUploadInProgress';

export const ImportEmployees = () => {
  const { primaryFrontColor } = useTheme();
  const language = useSelector(getApplicationLanguageId);
  const { data: groupSettings } = useGetBenefitGroupsQuery();
  const [cancelImport] = useCancelImportMutation();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [requestId, setRequestId] = useState<number | null>(null);
  const [ingestroError, setIngestroError] = useState<Error | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const importStatus = useSelector(selectImportStatus);
  const { data: customerData, isLoading: isLoadingCustomerData } =
    useGetDetailsQuery();
  const hasEmployees = customerData?.isEmployeesSet;
  useImportEmployees({
    ingestroError,
    setIngestroError
  });

  const groups = groupSettings?.map(group => ({
    label: group.groupName,
    value: group.groupName,
    alternativeMatches: [],
    type: 'string' as const
  })) as DropdownOptionAPI[];

  const isPopoverOpen = Boolean(anchorEl);

  const openDropdown = event => {
    setAnchorEl(event.currentTarget);
  };
  const closeDropdown = () => setAnchorEl(null);

  return (
    <PagePanel
      title={t('containers.beneficiaries.import_employees.title')}
      backIcon
      backIconLink={routerPaths.beneficiaries}
      headerContent={
        ingestroError ? null : (
          <Button
            id="download-template-button"
            mode={ButtonMode.Secondary}
            size={ButtonSize.Medium}
            onClick={openDropdown}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <DownloadIcon />
              {t('containers.beneficiaries.import_employees.download_button')}
            </Box>
          </Button>
        )
      }
      footer={
        uploadInProgress && requestId
          ? {
              hideSubmitButton: true,
              backText: t(
                'containers.beneficiaries.import_employees.in_progress.cancel'
              ),
              onBack: () => {
                cancelImport({ requestId });
                setUploadInProgress(false);
              }
            }
          : undefined
      }
    >
      {ingestroError && <IngestroError />}
      {isLoadingCustomerData && <LoadingSpinner />}
      {importStatus?.isAnyImportInProgress && !isLoadingCustomerData && (
        <AnotherUploadInProgress hasEmployees={hasEmployees} />
      )}
      <Box
        display={
          ingestroError || importStatus?.isAnyImportInProgress
            ? 'none'
            : undefined
        }
      >
        <Box pb={6}>
          <Notification
            id="import-employees-info"
            type={NotificationType.Info}
            text={
              <Trans
                i18nKey={
                  uploadInProgress
                    ? t(
                        'containers.beneficiaries.import_employees.in_progress.notification'
                      )
                    : t('containers.beneficiaries.import_employees.info')
                }
                components={{
                  a: (
                    <SimpleLink
                      target="_blank"
                      rel="noreferrer"
                      href={t(
                        'containers.beneficiaries.import_employees.info_url'
                      )}
                    />
                  )
                }}
              />
            }
          />
        </Box>
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={closeDropdown}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          disableAutoFocus
        >
          <Box display="flex" flexDirection="column">
            <FileFormatButton
              id="file-format-button-xlxs"
              href={getTemplateUrl(language, FileFormatEnum.XLSX)}
            >
              <Box display="flex" gap={1.5} py={1} px={2}>
                <DocumentIcon color={primaryFrontColor} />
                {t(
                  'containers.beneficiaries.import_employees.file_format.xlxs'
                )}
              </Box>
            </FileFormatButton>
            <FileFormatButton
              id="file-format-button-csv"
              href={getTemplateUrl(language, FileFormatEnum.CSV)}
            >
              <Box display="flex" gap={1.5} py={1} px={2}>
                <DocumentIcon color={primaryFrontColor} />
                {t('containers.beneficiaries.import_employees.file_format.csv')}
              </Box>
            </FileFormatButton>
          </Box>
        </Popover>
        <IngestroWrapper className="import-employees-wrapper">
          {groups ? (
            <IngestroImporter
              groups={groups}
              uploadInProgress={uploadInProgress}
              setUploadInProgress={setUploadInProgress}
              requestId={requestId}
              setRequestId={setRequestId}
            />
          ) : (
            <LoadingSpinner />
          )}
        </IngestroWrapper>
      </Box>
    </PagePanel>
  );
};
